import { Card } from "@shared/components/ui/card";
import { format } from "date-fns";
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  Clock,
  Eye,
  MapPin,
  Truck,
  User,
} from "lucide-react";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import PickupAction from "./PickupAction";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import GeneratebonDeLivraison from "./GeneratebonDeLivraison";
import AssignCollectorToPickupDialog from "./AssignCollectorToPickupDialog";
import CollectorPickupStatusUpdateDialog from "@features/packages/components/CollectorPickupStatusUpdateDialog";
import { cn } from "@/lib/utils";

const getStatusIcon = (status) => {
  switch (status) {
    case "pending":
      return <Clock className="h-3.5 w-3.5 text-yellow-500" />;
    case "picked_up":
    case "in_transit":
      return <Truck className="h-3.5 w-3.5 text-blue-500" />;
    case "signed":
    case "approved":
      return <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />;
    case "return_collection":
    case "rejected":
      return <AlertCircle className="h-3.5 w-3.5 text-red-500" />;
    default:
      return <MapPin className="h-3.5 w-3.5 text-primary" />;
  }
};

const getStatusBgColor = (status) => {
  switch (status) {
    case "pending":
      return "bg-yellow-100";
    case "picked_up":
    case "in_transit":
      return "bg-blue-100";
    case "signed":
    case "approved":
      return "bg-green-100";
    case "return_collection":
    case "rejected":
      return "bg-red-100";
    default:
      return "bg-gray-100";
  }
};

const getStatusTextColor = (status) => {
  switch (status) {
    case "pending":
      return "text-yellow-700";
    case "picked_up":
    case "in_transit":
      return "text-blue-700";
    case "signed":
    case "approved":
      return "text-green-700";
    case "return_collection":
    case "rejected":
      return "text-red-700";
    default:
      return "text-gray-700";
  }
};

const PickupCard = ({ pickup, viewMode = "row" }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();

  // Row View
  if (viewMode === "row") {
    return (
      <Card className="w-full transition-all duration-200 hover:shadow-md hover:border-primary/20 group">
        <div className="p-4 flex items-center gap-4">
          {/* Status Icon */}
          <div className={`flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-lg ${getStatusBgColor(pickup.status)} transition-transform group-hover:scale-105`}>
            {getStatusIcon(pickup.status)}
          </div>

          {/* Type & Status */}
          <div className="flex-shrink-0 min-w-[140px] space-y-1.5">
            <div className="font-semibold text-sm">
              {pickup.type === "package"
                ? t("pickup.packagePickup")
                : t("pickup.productPickup")}
            </div>
            <Badge
              variant="outline"
              className={`${getStatusBgColor(
                pickup.status
              )} ${getStatusTextColor(pickup.status)} border-0 text-xs font-medium`}
            >
              {pickup.status?.replace(/_/g, " ").toUpperCase()}
            </Badge>
          </div>

          {/* Contact Info */}
          <div className="flex-shrink-0 min-w-[160px] border-l pl-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <User className="h-3.5 w-3.5" />
              <span>{t("pickup.contact")}</span>
            </div>
            <div className="text-sm font-medium truncate">
              {pickup.name || pickup.user?.fullName}
            </div>
            <div className="text-xs text-muted-foreground">{pickup.phone}</div>
          </div>

          {/* Location */}
          <div className="flex-1 min-w-[180px] border-l pl-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <MapPin className="h-3.5 w-3.5" />
              <span>{t("pickup.location")}</span>
            </div>
            <div className="text-sm font-medium">{pickup.pickupCity}</div>
            <div className="text-xs text-muted-foreground truncate" title={pickup.address}>
              {pickup.address}
            </div>
          </div>

          {/* Meta Info */}
          <div className="flex-shrink-0 border-l pl-4 space-y-2">
            <div className="flex items-center gap-1.5 text-xs">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-medium">{format(new Date(pickup.updatedAt), "MMM dd")}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span className="font-semibold text-primary">
                {pickup.type === "package"
                  ? `${pickup.packages?.length || pickup.quantity || 0} ${t("common.packages")}`
                  : `${pickup.products?.length || pickup.quantity || 0} ${t("products.products")}`}
              </span>
            </div>
            {pickup.collector && (
              <div className="flex items-center gap-1.5 text-xs">
                <Truck className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-medium truncate max-w-[100px]" title={pickup.collector.fullName}>
                  {pickup.collector.fullName}
                </span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex-shrink-0 flex items-center gap-1.5 border-l pl-4">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate(`/pickups/${pickup._id}`)}
              className="h-9 w-9 p-0"
            >
              <Eye className="h-4 w-4" />
            </Button>

            {(auth.isCollector || auth.isAdmin || auth.isManager) && (
              <CollectorPickupStatusUpdateDialog
                pickupData={pickup}
                trigger={
                  <Button size="sm" variant="outline" className="h-9">
                    <Clock className="h-3.5 w-3.5" />
                  </Button>
                }
              />
            )}

            {(auth.isAdmin || auth.isManager) && (
              <>
                <AssignCollectorToPickupDialog
                  pickupData={pickup}
                  trigger={
                    <Button size="sm" variant="outline" className="h-9">
                      {pickup.collector ? (
                        <User className="h-3.5 w-3.5" />
                      ) : (
                        <Truck className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  }
                />
                <GeneratebonDeLivraison pickup={pickup} />
                {pickup.status === "pending" && (
                  <PickupAction
                    pickupId={pickup._id}
                    initialQuantity={pickup?.quantity}
                  />
                )}
              </>
            )}

            {pickup.note && (
              <div
                className="w-2 h-2 rounded-full bg-blue-500 animate-pulse ml-1"
                title={pickup.note}
              />
            )}
          </div>
        </div>
      </Card>
    );
  }

  // Card View
  return (
    <Card className="w-full transition-all duration-200 hover:shadow-md hover:border-primary/20 group overflow-hidden">
      <div className="p-4">
        {/* Header Section */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            {/* Status Icon with animation */}
            <div className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg ${getStatusBgColor(pickup.status)} transition-transform group-hover:scale-110`}>
              {getStatusIcon(pickup.status)}
            </div>
            
            {/* Type & Title */}
            <div className="space-y-1">
              <div className="font-semibold text-base">
                {pickup.type === "package"
                  ? t("pickup.packagePickup")
                  : t("pickup.productPickup")}
              </div>
              <Badge
                variant="outline"
                className={`${getStatusBgColor(
                  pickup.status
                )} ${getStatusTextColor(pickup.status)} border-0 text-xs font-medium`}
              >
                {pickup.status?.replace(/_/g, " ").toUpperCase()}
              </Badge>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate(`/pickups/${pickup._id}`)}
              className="h-8 w-8 p-0"
            >
              <Eye className="h-4 w-4" />
            </Button>
            {pickup.note && (
              <div
                className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"
                title={pickup.note}
              />
            )}
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          {/* Contact Info */}
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <User className="h-3.5 w-3.5" />
              <span className="font-medium">{t("pickup.contact")}</span>
            </div>
            <div className="text-sm font-medium truncate">
              {pickup.name || pickup.user?.fullName}
            </div>
            <div className="text-xs text-muted-foreground">{pickup.phone}</div>
          </div>

          {/* Location */}
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" />
              <span className="font-medium">{t("pickup.location")}</span>
            </div>
            <div className="text-sm font-medium">{pickup.pickupCity}</div>
            <div className="text-xs text-muted-foreground truncate" title={pickup.address}>
              {pickup.address}
            </div>
          </div>
        </div>

        {/* Meta Information Bar */}
        <div className="flex items-center justify-between py-2 px-3 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-xs">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-medium">{format(new Date(pickup.updatedAt), "MMM dd, yyyy")}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              {pickup.type === "package" ? (
                <>
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  <span className="font-semibold text-primary">
                    {pickup.packages?.length || pickup.quantity || 0}{" "}
                    {t("common.packages")}
                  </span>
                </>
              ) : (
                <>
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  <span className="font-semibold text-primary">
                    {pickup.products?.length || pickup.quantity || 0}{" "}
                    {t("products.products")}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Collector Info */}
          {pickup.collector && (
            <div className="flex items-center gap-1.5 text-xs">
              <Truck className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-medium">{pickup.collector.fullName}</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t">
          {(auth.isCollector || auth.isAdmin || auth.isManager) && (
            <CollectorPickupStatusUpdateDialog
              pickupData={pickup}
              trigger={
                <Button size="sm" variant="outline" className="flex-1">
                  <Clock className="h-3.5 w-3.5 mr-1.5" />
                  {t("pickup.updateStatus")}
                </Button>
              }
            />
          )}

          {(auth.isAdmin || auth.isManager) && (
            <>
              <AssignCollectorToPickupDialog
                pickupData={pickup}
                trigger={
                  <Button size="sm" variant="outline" className="flex-1">
                    {pickup.collector ? (
                      <>
                        <User className="h-3.5 w-3.5 mr-1.5" />
                        {t("pickup.reassign")}
                      </>
                    ) : (
                      <>
                        <Truck className="h-3.5 w-3.5 mr-1.5" />
                        {t("pickup.assign")}
                      </>
                    )}
                  </Button>
                }
              />
              <GeneratebonDeLivraison pickup={pickup} />
              {pickup.status === "pending" && (
                <PickupAction
                  pickupId={pickup._id}
                  initialQuantity={pickup?.quantity}
                />
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

export default PickupCard;
